import { showUserAlert } from "../utils/userErrorMessage";
import React, { useEffect, useState } from "react";
import Header from "../Component/Header";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { useNavigate } from "react-router-dom";
import {
  approveAiApproval,
  canApproveAiOps,
  fetchAiApprovals,
  getBugReportItems,
  getDriverChangeItems,
  getDriverCreateItems,
  getExpenseItems,
  getUserCreateItems,
  getVehicleChangeItems,
  rejectAiApproval,
} from "../services/aiOps/aiOpsService";

const statusLabels = {
  pending: "대기",
  applied: "적용",
  rejected: "반려",
  failed: "실패",
};

function formatDateTime(value) {
  if (!value) return "";
  return new Date(value).toLocaleString("ko-KR", { hour12: false });
}

function ApprovalDetail({ approval }) {
  if (!approval) return null;

  const updates = approval.after_data?.reservation_updates || [];
  const reservations = approval.after_data?.reservations || approval.payload?.reservations || [];
  const selected = approval.after_data?.selected_candidate || {};
  const safety = approval.payload?.safety || approval.after_data?.safety || {};
  const expense = approval.payload?.expense || approval.after_data?.expense || {};
  const expenseItems = getExpenseItems(expense);
  const vehicleBefore = approval.before_data?.car || {};
  const vehicleUpdates = approval.payload?.updates || {};
  const vehicleChanges = approval.after_data?.changes || getVehicleChangeItems(vehicleBefore, vehicleUpdates);
  const vehicleCreate = approval.payload?.vehicle || approval.after_data?.vehicle || {};
  const driverBefore = approval.before_data?.driver || {};
  const driverUpdates = approval.payload?.updates || {};
  const driverChanges = approval.after_data?.changes || getDriverChangeItems(driverBefore, driverUpdates);
  const driverCreate = approval.payload?.driver || approval.after_data?.driver || {};
  const driverCreateItems = getDriverCreateItems(driverCreate);
  const userCreate = approval.payload?.user || approval.after_data?.user || {};
  const userCreateItems = getUserCreateItems(userCreate);
  const bugReport = approval.payload?.report || approval.after_data?.report || {};
  const bugReportItems = getBugReportItems(bugReport);

  return (
    <div className="space-y-3 text-sm min-w-0 break-words">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <div className="text-gray-500">요청자</div>
          <div>{approval.requested_by || "-"}</div>
        </div>
        <div>
          <div className="text-gray-500">유형</div>
          <div>{approval.action_type}</div>
        </div>
        <div>
          <div className="text-gray-500">상태</div>
          <div>{statusLabels[approval.status] || approval.status}</div>
        </div>
      </div>

      {approval.action_type === "reservation_create" && (
        <div className="rounded border p-3">
          <div className="font-semibold mb-2">예약 초안</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
            <span className="text-gray-500">생성 예약</span>
            <span>{reservations.length}건</span>
            <span className="text-gray-500">시작일</span>
            <span>{approval.payload?.summary?.start_date || "-"}</span>
            <span className="text-gray-500">사용자</span>
            <span>{approval.payload?.summary?.user || "-"}</span>
            <span className="text-gray-500">경로</span>
            <span>{approval.payload?.summary?.start || "-"} → {approval.payload?.summary?.destination || "-"}</span>
          </div>
        </div>
      )}

      {approval.action_type === "driver_sick_reassign" && (
        <div className="rounded border p-3">
          <div className="font-semibold mb-2">재배정 초안</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
            <span className="text-gray-500">영향 예약</span>
            <span>{updates.length}건</span>
            <span className="text-gray-500">대체 기사</span>
            <span>{selected.driver || "-"}</span>
            <span className="text-gray-500">대체 차량</span>
            <span>{selected.car_number_for_reservation || selected.car_number || "-"}</span>
            <span className="text-gray-500">휴무 구분</span>
            <span>{approval.payload?.leave?.name || "-"}</span>
          </div>
        </div>
      )}

      {approval.action_type === "safety_create" && (
        <div className="rounded border p-3">
          <div className="font-semibold mb-2">안전관리 등록 초안</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
            <span className="text-gray-500">형태</span>
            <span>{safety.issue || "-"}</span>
            <span className="text-gray-500">운전자</span>
            <span>{safety.driver_name || "-"}</span>
            <span className="text-gray-500">자기 차량</span>
            <span>{safety.car_number || "-"}</span>
            <span className="text-gray-500">상대 차량</span>
            <span>{safety.opponent || "-"}</span>
            <span className="text-gray-500">일시</span>
            <span>{safety.date || "-"}</span>
            <span className="text-gray-500">장소</span>
            <span>{safety.place || "-"}</span>
            <span className="text-gray-500">개요</span>
            <span>{safety.summary || "-"}</span>
          </div>
        </div>
      )}

      {approval.action_type === "expense_create" && (
        <div className="rounded border p-3">
          <div className="font-semibold mb-2">지출 등록 초안</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
            <span className="text-gray-500">지출일</span>
            <span>{expense.date || "-"}</span>
            <span className="text-gray-500">차량</span>
            <span>{expense.car_number || "-"}</span>
            <span className="text-gray-500">기사</span>
            <span>{expense.human_name || "-"}</span>
            {expenseItems.length > 0 ? (
              expenseItems.map((item) => (
                <React.Fragment key={item.key}>
                  <span className="text-gray-500">{item.label}</span>
                  <span>{item.amount.toLocaleString()}원</span>
                </React.Fragment>
              ))
            ) : (
              <>
                <span className="text-gray-500">지출 항목</span>
                <span>-</span>
              </>
            )}
            <span className="text-gray-500">비고</span>
            <span>{expense.bigo || "-"}</span>
          </div>
        </div>
      )}

      {approval.action_type === "vehicle_update" && (
        <div className="rounded border p-3">
          <div className="font-semibold mb-2">차량 정보 수정 초안</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
            {vehicleChanges.map((item) => (
              <React.Fragment key={item.key}>
                <span className="text-gray-500">{item.label}</span>
                <span>{String(item.before || "-")} → {String(item.after || "-")}</span>
              </React.Fragment>
            ))}
          </div>
        </div>
      )}

      {approval.action_type === "vehicle_create" && (
        <div className="rounded border p-3">
          <div className="font-semibold mb-2">차량 추가 등록 초안</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
            <span className="text-gray-500">차량번호</span>
            <span>{vehicleCreate.car_number || "-"}</span>
            <span className="text-gray-500">차명</span>
            <span>{vehicleCreate.car_name || "-"}</span>
            <span className="text-gray-500">기사명</span>
            <span>{vehicleCreate.driver || "-"}</span>
            <span className="text-gray-500">핸드폰</span>
            <span>{vehicleCreate.phone || "-"}</span>
            <span className="text-gray-500">기사 이메일</span>
            <span>{vehicleCreate.driver_email || "-"}</span>
            <span className="text-gray-500">년식</span>
            <span>{vehicleCreate.year || "-"}</span>
            <span className="text-gray-500">승차인원</span>
            <span>{vehicleCreate.seat || "-"}</span>
          </div>
        </div>
      )}

      {approval.action_type === "driver_update" && (
        <div className="rounded border p-3">
          <div className="font-semibold mb-2">기사 정보 수정 초안</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
            {driverChanges.map((item) => (
              <React.Fragment key={item.key}>
                <span className="text-gray-500">{item.label}</span>
                <span>{String(item.before || "-")} → {String(item.after || "-")}</span>
              </React.Fragment>
            ))}
          </div>
        </div>
      )}

      {approval.action_type === "driver_create" && (
        <div className="rounded border p-3">
          <div className="font-semibold mb-2">기사 추가 등록 초안</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
            {driverCreateItems.map((item) => (
              <React.Fragment key={item.key}>
                <span className="text-gray-500">{item.label}</span>
                <span>{String(item.value || "-")}</span>
              </React.Fragment>
            ))}
          </div>
        </div>
      )}

      {approval.action_type === "user_create" && (
        <div className="rounded border p-3">
          <div className="font-semibold mb-2">사용자 추가 등록 초안</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
            {userCreateItems.map((item) => (
              <React.Fragment key={item.key}>
                <span className="text-gray-500">{item.label}</span>
                <span>{item.key === "password" ? "****" : String(item.value || "-")}</span>
              </React.Fragment>
            ))}
          </div>
        </div>
      )}

      {approval.action_type === "bug_report_create" && (
        <div className="rounded border p-3">
          <div className="font-semibold mb-2">버그 신고 초안</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
            {bugReportItems.map((item) => (
              <React.Fragment key={item.key}>
                <span className="text-gray-500">{item.label}</span>
                <span className="whitespace-pre-wrap">{String(item.value || "-")}</span>
              </React.Fragment>
            ))}
          </div>
        </div>
      )}

      <div className="rounded border p-3 text-xs text-gray-700">
        <div className="font-semibold mb-1">검증</div>
        <pre className="whitespace-pre-wrap break-all">{JSON.stringify(approval.validation || {}, null, 2)}</pre>
      </div>
    </div>
  );
}

export default function AiApprovals() {
  const [status, setStatus] = useState("pending");
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [workingId, setWorkingId] = useState("");
  const approvable = canApproveAiOps();
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const data = await fetchAiApprovals(status);
      setRows(data);
      setSelected((prev) => data.find((row) => row.id === prev?.id) || data[0] || null);
    } catch (e) {
      showUserAlert("승인함 조회 실패: " + (e?.message || "알 수 없는 오류"));
      setRows([]);
      setSelected(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const handleApprove = async (approval) => {
    if (!approval) return;
    if (!window.confirm("이 변경안을 승인하고 실제 DB에 반영할까요?")) return;

    setWorkingId(approval.id);
    try {
      await approveAiApproval(approval.id);
      showUserAlert("승인 및 적용이 완료되었습니다.");
      if (approval.action_type === "expense_create") {
        const expense = approval.payload?.expense || approval.after_data?.expense || {};
        const params = new URLSearchParams();
        if (expense.date) {
          params.set("from", expense.date);
          params.set("to", expense.date);
        }
        if (expense.car_number) params.set("car", expense.car_number);
        if (expense.human_name) params.set("driver", expense.human_name);
        navigate(`/unpaid/car-expenses?${params.toString()}`);
        return;
      }
      if (approval.action_type === "vehicle_update") {
        const carId = approval.payload?.car_id;
        navigate(carId ? `/car-edit/${carId}` : "/car-info");
        return;
      }
      if (approval.action_type === "vehicle_create") {
        navigate("/car-info");
        return;
      }
      if (approval.action_type === "driver_update") {
        const profileId = approval.payload?.profile_id;
        navigate(profileId ? `/drivers/${profileId}/edit` : "/drivers");
        return;
      }
      if (approval.action_type === "driver_create") {
        navigate("/drivers");
        return;
      }
      if (approval.action_type === "user_create") {
        navigate("/user-management");
        return;
      }
      if (approval.action_type === "bug_report_create") {
        navigate("/bug-report");
        return;
      }
      await load();
    } catch (e) {
      showUserAlert("승인 실패: " + (e?.message || "알 수 없는 오류"));
    } finally {
      setWorkingId("");
    }
  };

  const handleReject = async (approval) => {
    if (!approval) return;
    const reason = window.prompt("반려 사유를 입력하세요.", "");
    if (reason === null) return;

    setWorkingId(approval.id);
    try {
      await rejectAiApproval(approval.id, reason);
      showUserAlert("반려되었습니다.");
      await load();
    } catch (e) {
      showUserAlert("반려 실패: " + (e?.message || "알 수 없는 오류"));
    } finally {
      setWorkingId("");
    }
  };

  return (
    <div className="space-y-4">
      <Header />
      <div className="p-4 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-lg font-semibold">AI 승인함</h2>
          <div className="flex flex-wrap gap-2">
            {["pending", "applied", "rejected", "all"].map((value) => (
              <button
                key={value}
                type="button"
                onClick={() => setStatus(value)}
                className={`rounded border px-3 py-2 text-sm ${
                  status === value ? "bg-blue-600 text-white" : "bg-white text-gray-700 hover:bg-gray-50"
                }`}
              >
                {value === "all" ? "전체" : statusLabels[value] || value}
              </button>
            ))}
          </div>
        </div>

        {!approvable && (
          <div className="rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
            현재 계정은 승인 권한이 없습니다.
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_420px] gap-4 min-w-0">
          <Card className="min-w-0">
            <CardContent className="bg-white">
              <div className="overflow-x-auto">
                <table className="ai-approvals-mobile-table table-auto w-full border text-sm">
                  <thead className="bg-blue-500 text-white">
                    <tr>
                      {["생성일", "상태", "제목", "요청자", "작업"].map((header) => (
                        <th key={header} className="border p-2">{header}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {loading && (
                      <tr>
                        <td colSpan={5} className="border p-4 text-center">불러오는 중...</td>
                      </tr>
                    )}
                    {!loading && rows.length === 0 && (
                      <tr>
                        <td colSpan={5} className="border p-4 text-center text-gray-500">데이터가 없습니다.</td>
                      </tr>
                    )}
                    {rows.map((row) => (
                      <tr
                        key={row.id}
                        className={selected?.id === row.id ? "bg-blue-50" : ""}
                      >
                        <td className="border p-2 whitespace-nowrap" data-label="생성일">{formatDateTime(row.created_at)}</td>
                        <td className="border p-2 whitespace-nowrap" data-label="상태">{statusLabels[row.status] || row.status}</td>
                        <td className="border p-2" data-label="제목">
                          <button
                            type="button"
                            onClick={() => setSelected(row)}
                            className="text-left text-blue-600 underline"
                          >
                            {row.title}
                          </button>
                        </td>
                        <td className="border p-2 whitespace-nowrap" data-label="요청자">{row.requested_by || "-"}</td>
                        <td className="border p-2 whitespace-nowrap" data-label="작업">
                          {row.status === "pending" ? (
                            <div className="flex gap-2">
                              <Button
                                onClick={() => handleApprove(row)}
                                disabled={!approvable || workingId === row.id}
                                className="px-3 py-1 text-xs disabled:opacity-50"
                              >
                                승인
                              </Button>
                              <button
                                type="button"
                                onClick={() => handleReject(row)}
                                disabled={!approvable || workingId === row.id}
                                className="rounded border px-3 py-1 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                              >
                                반려
                              </button>
                            </div>
                          ) : (
                            "-"
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          <Card className="min-w-0">
            <CardContent className="bg-white p-4">
              <div className="font-semibold mb-3">상세</div>
              {selected ? (
                <ApprovalDetail approval={selected} />
              ) : (
                <div className="text-sm text-gray-500">선택된 항목이 없습니다.</div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
